import FilterLink from "@/components/Blog/FilterLink";
import BaseImage from "@/components/ui/Image";
import { excludeBlogHiddenTags } from "@cgc/core/utils/tags";
import { Button } from "@cgc/ui/button";
import { Paragraph as BaseParagraph, ParagraphProps } from "@cgc/ui/typography";
import { clsx } from "@cgc/ui/utils/clsx";
import { format } from "date-fns";
import Link from "next/link";
import React from "react";

type ImageProps = {
  image: string;
  alt: string;
  priority?: boolean;
};

const Image: React.FC<ImageProps> = ({ image, alt, priority = false }) => {
  const props = {
    fill: true,
    sizes: [
      "(max-width: 768px) 100vw",
      "(max-width: 1200px) 50vw",
      "33vw",
    ].join(",\n"),
    priority,
  };

  return (
    <BaseImage
      src={image}
      {...props}
      alt={alt}
      className="object-cover object-center"
    />
  );
};

const Paragraph: React.FC<ParagraphProps> = (props) => (
  <>
    <BaseParagraph {...props} size={"p3"} mobileOnly inline />
    <BaseParagraph {...props} size={"p2"} desktopOnly inline />
  </>
);

type BlogCardProps = {
  title: string;
  image: string;
  pubDate: string;
  link: string;
  size?: "default" | "medium";
  tags: string[];
  priority?: boolean;
  showTags?: boolean;
  summary?: string;
  showReadMore?: boolean;
  borderColor?: "default" | "yellow";
  onTagClick?: (tag: string) => void;
};

const BlogCard: React.FC<BlogCardProps> = ({
  title,
  image,
  pubDate,
  link,
  size = "default",
  tags,
  priority = false,
  showTags = true,
  summary,
  showReadMore = true,
  borderColor = "default",
  onTagClick,
}) => {
  const isMedium = size === "medium";
  const allowedTags = excludeBlogHiddenTags(tags);

  return (
    <article
      className={clsx(
        "relative mx-auto mb-[30px] w-full max-w-[326px]",
        isMedium ? "sm:max-w-[516px] md:max-w-[790px]" : "sm:max-w-[516px]",
      )}
    >
      <Link
        href={link}
        className={clsx(
          "relative block h-[163px] sm:h-[266px]",
          isMedium && "md:h-[395px]",
        )}
      >
        <Image image={image} alt={title} priority={priority} />
      </Link>

      <div
        className={clsx(
          "relative z-10 -mt-[20px] w-full",
          isMedium ? "-mt-[62px]" : "-mt-[34px]",
        )}
      >
        <div
          className={clsx(
            "shadow-blog-hit mx-auto w-10/12 max-w-[623px] space-y-2 border-t-[15px] border-solid bg-white px-5 py-[10px] text-center",
            borderColor === "default" && "border-cgc-green",
            borderColor === "yellow" && "border-cgc-yellow-60 border-t-[6px]",
          )}
        >
          <BaseParagraph size="p3" className="text-cgc-blue-80">
            {format(new Date(pubDate), "MMMM d, yyyy")}
          </BaseParagraph>
          <Link href={link} className="block no-underline decoration-0">
            <BaseParagraph
              size={size === "medium" ? "sub2" : "p1"}
              as="span"
              className="font-bold"
            >
              {title}
            </BaseParagraph>
          </Link>
          {summary && (
            <Paragraph size="p1" className="my-2.5">
              {summary}
            </Paragraph>
          )}
          {allowedTags.length > 0 && showTags && (
            <div className="text-cgc-blue text-center">
              <Paragraph as="span">Tagged: </Paragraph>
              {allowedTags.map((tag, index, collection) => (
                <FilterLink
                  param="tag"
                  value={tag}
                  key={`tagSearch:${link}:${tag}:${index}`}
                  className="text-cgc-blue mr-1.5 underline"
                  onClick={onTagClick}
                >
                  <Paragraph as="span">
                    {tag}
                    {index !== collection.length - 1 ? "," : ""}
                  </Paragraph>
                </FilterLink>
              ))}
            </div>
          )}
          {showReadMore && (
            <div className="text-center">
              <Link href={link}>
                <Button
                  color="primary"
                  className="capitalize"
                  typoComponent={() => <Paragraph>Read More</Paragraph>}
                />
              </Link>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
